import { useCallback } from 'react';
import { KTag } from '@/ui-library/components/atoms/KTag';
import { EMPLOYMENT_TYPE_CONFIG } from '@/constants/requisition-constants';

// Function to format location values
const formatLocationValue = (value: string, groupType?: string) => {
  if (groupType === 'interview_location') {
    if (value === undefined || value === null || value === '') {
      return 'No location';
    }

    const locationMap: Record<string, string> = {
      'No location': 'No location',
      zoom: 'Zoom',
      onsite: 'Onsite',
      google_meet: 'Google Meet',
      phone_interview: 'Phone Interview',
      teams: 'Teams',
    };

    return locationMap[value] || value;
  }
  return value;
};

const formatJobRole = (role: string) => {
  const jobRoleMap: Record<string, string> = {
    primary_hiring_manager: 'Hiring manager',
    primary_recruiter: 'Recruiter',
    coordinator: 'Coordinator',
    sourcer: 'External recruiter',
    hiring_manager: 'Hiring manager',
    recruiter: 'Recruiter',
    unknown: 'Users with manage job access',
  };
  return jobRoleMap[role] || role;
};

const formatStagesRole = (role: string) => {
  const stageMap: Record<string, string> = {
    Offer: 'Offer stages',
    Lead: 'Lead stages',
    Applied: 'Application review stages',
    Interview: 'Interview stages',
    Hired: 'Hired stages',
  };
  return stageMap[role] || role;
};

// Format header value based on group type
const formatHeaderValue = (
  value: string | { id: number; name: string; deleted: boolean },
  isLocation?: boolean,
  isJobRole?: boolean,
  isStage?: boolean,
  isWorkType?: boolean
) => {
  if (isLocation) {
    return formatLocationValue(value as string, 'interview_location');
  }
  if (isJobRole) {
    return formatJobRole(value as string);
  }
  if (isStage) {
    if (typeof value === 'object' && value !== null && 'name' in value) {
      return formatStagesRole(value.name);
    }
    return formatStagesRole(value as string);
  }
  if (isWorkType) {
    return EMPLOYMENT_TYPE_CONFIG[value as string]?.label;
  }
  return typeof value === 'object' ? value?.name : value;
};

export const useDrillDownHandler = (
  feDrilldown: boolean,
  onDrillDownOpen: (data: any) => void,
  isJobFiltered?: boolean
) => {
  const handleDrillDown = useCallback(
    (
      row: any,
      metric: string,
      isTotal = false,
      group: any,
      tableHeader?: string,
      metricValue?: any
    ) => {
      const hasSubRows = row?.subRows.length > 0;
      const rowHeader = row?.header;
      const groupby2 =
        group?.group_by_2 === null || group?.group_by_2?.id === null;

      // Check if we're grouping by location
      const isG1Location = group?.group_by_1?.id === 'interview_location';
      const isG2Location = group?.group_by_2?.id === 'interview_location';
      const isG1JobRole =
        group?.group_by_1?.id === 'action_performed_by_user_role';
      const isG1Stage = group?.group_by_1?.id === 'stage';
      const isG2Stage = group?.group_by_2?.id === 'stage';
      const isG1WorkType =
        group?.group_by_1?.id === 'work_type' ||
        group?.group_by_1?.id === 'job_type';
      const isG2WorkType =
        group?.group_by_2?.id === 'work_type' ||
        group?.group_by_2?.id === 'job_type';
      const shouldSkipFormatting =
        (group?.group_by_1?.id === 'job_name' && isG2Stage) ||
        (isJobFiltered && isG1Stage && !isG2Stage);

      const getG1Header = () => {
        if (isTotal) return 'Total';
        if (groupby2) {
          const headerValue = isG1Stage ? row?.g1 : rowHeader;
          return formatHeaderValue(
            headerValue,
            isG1Location,
            isG1JobRole,
            !shouldSkipFormatting,
            isG1WorkType
          );
        }
        if (!groupby2 && hasSubRows) {
          const headerValue = isG1Stage ? row?.g1 : rowHeader;
          return formatHeaderValue(
            headerValue,
            isG1Location,
            isG1JobRole,
            !shouldSkipFormatting,
            isG1WorkType
          );
        }
        if (!groupby2 && !hasSubRows) {
          const headerValue = isG1Stage
            ? row?.g1
            : row?.parentHeader || row?.dt;
          return formatHeaderValue(
            headerValue,
            isG1Location,
            isG1JobRole,
            !shouldSkipFormatting,
            isG1WorkType
          );
        }
      };

      const getG2Header = () => {
        if (isTotal) return null;
        if (groupby2) return null;
        if (!groupby2 && hasSubRows) return null;
        if (!groupby2 && !hasSubRows) {
          const headerValue = isG2Stage ? row?.g2 : rowHeader;
          return formatHeaderValue(
            headerValue,
            isG2Location,
            false,
            !shouldSkipFormatting,
            isG2WorkType
          );
        }
      };

      const isG1Deleted = isG1Stage && row?.g1?.deleted;
      const isG2Deleted = isG2Stage && row?.g2?.deleted;
      const isDeleted = isG1Deleted || isG2Deleted;

      const rowData = {
        dt: isTotal ? undefined : row?.dt,
        type: 'table',
        g1: isTotal ? undefined : row?.g1 || null,
        key: isTotal ? 'tableTotal' : null,
        tableHeader: tableHeader,
        metricValue: metricValue,
        metric,
        g1Header: getG1Header(),
        g2Header: getG2Header(),
        ...(row?.subRows.length === 0 && !isTotal && { g2: row?.g2 || null }),
        ...(row?.subRows.length > 0 && { isParentRow: true }),
        ...(isDeleted && {
          deletedTag: (
            <KTag
              value="Deleted"
              bgColor="pastel-1-light"
              color="pastel-1-dark"
            />
          ),
          isG1Deleted,
          isG2Deleted,
        }),
      };

      if (feDrilldown) {
        onDrillDownOpen(rowData);
      }
    },
    [feDrilldown, onDrillDownOpen, isJobFiltered]
  );

  return { handleDrillDown };
};
